import type { SiteLayer } from '../../types/site';

/**
 * Generation & Impact layout layers are materialized map views owned by the
 * publication workflow. They must be replaced by that workflow, not edited as
 * ordinary user-authored map geometry.
 *
 * `genimpact-*` is retained as a defensive legacy signal because layouts
 * published before the versioned metadata contract do not carry `layoutKind`.
 */
export function isManagedGenerationImpactLayer(layer: SiteLayer): boolean {
  const publicationOwned = layer.source === 'scenario-derived'
    && layer.attributes?.layoutKind === 'generation-impact';
  return publicationOwned || layer.id.startsWith('genimpact-');
}

/**
 * Preserve the last loaded managed-layer representation across a full-array
 * replacement. Previous managed rows win by id (so a stale editor cannot
 * rename or reshape them), and managed rows omitted by the replacement are
 * restored. Non-managed rows retain the replacement's order and semantics.
 */
export function preserveManagedGenerationImpactLayers(
  previous: readonly SiteLayer[] | undefined,
  replacement: readonly SiteLayer[],
): SiteLayer[] {
  const previousManaged = new Map(
    (previous ?? [])
      .filter(isManagedGenerationImpactLayer)
      .map((layer) => [layer.id, layer]),
  );
  if (previousManaged.size === 0) return [...replacement];

  const next = replacement.map((layer) => previousManaged.get(layer.id) ?? layer);
  const nextIds = new Set(next.map((layer) => layer.id));
  for (const layer of previous ?? []) {
    if (!previousManaged.has(layer.id) || nextIds.has(layer.id)) continue;
    next.push(layer);
    nextIds.add(layer.id);
  }
  return next;
}
